import React, { useEffect, useRef, useState } from "react";
import { Pencil, Check, X, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const frToIso = (v) => { const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v || ""); return m ? `${m[3]}-${m[2]}-${m[1]}` : ""; };
const isoToFr = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || ""); return m ? `${m[3]}/${m[2]}/${m[1]}` : ""; };

export function EditableField({ label, value, display, icon: Icon, link, bold, span2, onSave, onDelete, autoEdit, testid, dateFr }) {
  const [editing, setEditing] = useState(!!autoEdit);
  const [draft, setDraft] = useState(value || "");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    if ((draft || "") === (value || "")) return setEditing(false);
    setBusy(true);
    try {
      await onSave(draft);
      setEditing(false);
      toast.success(`${label} mis à jour`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  const shown = display ?? value;
  return (
    <div className={`group min-w-0 ${span2 ? "col-span-2" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-norea-textMuted mb-0.5">
        <span className="truncate">{label}</span>
        {!editing && (
          <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-norea-textMuted hover:text-norea-primary" title="Modifier" data-testid={`${testid}-edit-btn`}>
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {!editing && onDelete && (
          <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-norea-textMuted hover:text-red-600" title="Supprimer ce champ de tous les dossiers" data-testid={`${testid}-delete-btn`}>
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <input
            ref={inputRef}
            type={dateFr ? "date" : "text"}
            value={dateFr ? frToIso(draft) : draft}
            onChange={(e) => setDraft(dateFr ? isoToFr(e.target.value) : e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            className="flex-1 min-w-0 px-2 py-1 text-sm border border-norea-primary rounded bg-white outline-none focus:ring-1 focus:ring-norea-primary"
            data-testid={`${testid}-input`}
          />
          <button onClick={save} disabled={busy} className="p-1 rounded text-emerald-700 hover:bg-emerald-50" data-testid={`${testid}-save-btn`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setEditing(false)} className="p-1 rounded text-norea-textMuted hover:bg-norea-surfaceHover" data-testid={`${testid}-cancel-btn`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className={`text-sm flex items-center gap-1.5 min-w-0 ${bold ? "font-medium" : ""} text-norea-text`}>
          {Icon && <Icon className="w-3.5 h-3.5 text-norea-textDisabled flex-shrink-0" />}
          {link && value ? (
            <a href={link} className="truncate hover:text-norea-primary hover:underline" title={shown}>{shown}</a>
          ) : (
            <span className="truncate" title={shown || undefined} data-testid={`${testid}-value`}>{shown || "—"}</span>
          )}
        </div>
      )}
    </div>
  );
}

export function AddFieldButton({ onCreate, testid }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!label.trim()) return;
    setBusy(true);
    try {
      await onCreate(label.trim());
      setLabel("");
      setOpen(false);
      toast.success("Champ ajouté à tous les dossiers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="col-span-2 mt-1 inline-flex items-center gap-1.5 text-xs text-norea-textMuted hover:text-norea-primary w-fit" data-testid={testid}>
        <Plus className="w-3.5 h-3.5" /> Ajouter un champ
      </button>
    );
  }
  return (
    <div className="col-span-2 mt-1 flex items-center gap-1.5">
      <input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") create(); if (e.key === "Escape") setOpen(false); }}
        placeholder="Nom du champ (ex. Date de naissance)"
        className="flex-1 min-w-0 px-2 py-1 text-sm border border-norea-border rounded bg-white outline-none focus:border-norea-primary"
        data-testid={`${testid}-input`}
      />
      <button onClick={create} disabled={busy || !label.trim()} className="btn-primary text-xs disabled:opacity-50" data-testid={`${testid}-confirm`}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Ajouter"}
      </button>
      <button onClick={() => setOpen(false)} className="p-1 rounded text-norea-textMuted hover:bg-norea-surfaceHover" data-testid={`${testid}-cancel`}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
